import React, { useMemo } from 'react';
import { GUEST_QUESTION_ELEMENTS } from '@repo/schemas';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import usePage from '@dashboard/hooks/usePage';
import { guestQuestionsKey } from '@dashboard/lib/queryKeys';
import { fetcher } from '@dashboard/services/request';
import useGuestQuestions, { guestQuestionsPath } from '../useGuestQuestions';
import { getGuestQuestionPresets } from '@dashboard/services/pageFieldTypes';

import FieldsList from '../../../components/PageFieldsForm/components/FieldsList';
import { FieldsListSkeleton } from '../../../components/PageFieldsForm/components/FieldsListSkeleton';
import { FieldsProvider } from '../../../components/PageFieldsForm/FieldsContext';
import { FieldEditProvider } from '../../../components/PageFieldsForm/FieldEditContext';
import { FormLayoutProvider } from '../../../components/PageFieldsForm/FormLayoutContext';
import { FieldSourceProvider } from '../../../components/PageFieldsForm/FieldSourceContext';

const LOCKED_TYPES = ['name', 'email'];

const asResult = promise =>
  promise.then(
    data => ({ data, error: null }),
    error => ({ data: null, error: error?.message ? error : 'Unable to save question' }),
  );

export function useGuestQuestionSource() {
  const { page } = usePage();
  const { sellerId, token } = useAuth();
  const queryClient = useQueryClient();
  const base = guestQuestionsPath(sellerId, page._id);
  const { data: questions, isLoading } = useGuestQuestions(page._id);

  const refresh = () => queryClient.invalidateQueries({ queryKey: guestQuestionsKey(page._id) });

  const source = useMemo(
    () => ({
      hasLayout: false,
      fields: questions,
      allowedElements: GUEST_QUESTION_ELEMENTS,
      presets: getGuestQuestionPresets(),
      saveField: (values, fieldId) =>
        asResult(
          fetcher(fieldId ? `${base}/${fieldId}` : base, token, {
            method: fieldId ? 'PUT' : 'POST',
            body: JSON.stringify(values),
          }),
        ),
      deleteField: fieldId =>
        fetcher(`${base}/${fieldId}`, token, { method: 'DELETE' }).catch(() => false),
      saveLayout: layout =>
        fetcher(`${base}/layout`, token, { method: 'POST', body: JSON.stringify({ layout }) }).then(
          saved => {
            queryClient.setQueryData(guestQuestionsKey(page._id), saved.questions);
            return saved.guestFormLayout;
          },
        ),
      refresh,
      layoutBlocks: true,
      isLocked: field => LOCKED_TYPES.includes(field?.type),
      isUndeletable: field => field?.type === 'email',
      noun: 'question',
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [questions, base, token, page._id],
  );

  return { source, isLoading };
}

const GuestQuestionsForm = () => {
  const { source, isLoading } = useGuestQuestionSource();

  if (isLoading || !source.fields) return <FieldsListSkeleton rows={2} />;

  return (
    <FieldSourceProvider source={source}>
      <FieldsProvider>
        <FormLayoutProvider
          layoutKey="attendeeFormLayout"
          saveLayoutRequest={source.saveLayout}
          pageForm={false}
        >
          <FieldEditProvider>
            <FieldsList />
          </FieldEditProvider>
        </FormLayoutProvider>
      </FieldsProvider>
    </FieldSourceProvider>
  );
};

export default GuestQuestionsForm;
